import {
  isCondition,
  isGroup,
  type Filter,
  type FilterCondition,
  type FilterGroup,
  type FilterNode,
} from "@estelle/domain";

/**
 * Immutable edits to a filter tree, addressed by path.
 *
 * A path is the list of child indexes from the root — `[1, 0]` is the first
 * child of the second child. The tree is nested, and React state has to be
 * replaced rather than mutated, so every edit rebuilds the spine down to the
 * node it touches and shares everything else.
 */
export type Path = number[];

function replaceChild(
  group: FilterGroup,
  index: number,
  next: FilterNode | null,
): FilterGroup {
  const children =
    next === null
      ? group.children.filter((_, i) => i !== index)
      : group.children.map((child, i) => (i === index ? next : child));
  return { ...group, children };
}

/** Apply `edit` to the node at `path`. Returning null removes it. */
export function updateAt(
  root: Filter,
  path: Path,
  edit: (node: FilterNode) => FilterNode | null,
): Filter {
  if (path.length === 0) {
    const next = edit(root);
    return next && isGroup(next) ? next : root;
  }

  function walk(node: FilterNode, depth: number): FilterNode | null {
    if (!isGroup(node)) return node;

    const index = path[depth];
    if (index === undefined) return node;

    const child = node.children[index];
    if (!child) return node;

    const next =
      depth === path.length - 1 ? edit(child) : walk(child, depth + 1);
    return replaceChild(node, index, next);
  }

  const result = walk(root, 0);
  return result && isGroup(result) ? result : root;
}

/** Append a node to the group at `path`. */
export function appendAt(root: Filter, path: Path, node: FilterNode): Filter {
  return updateAt(root, path, (target) =>
    isGroup(target)
      ? { ...target, children: [...target.children, node] }
      : target,
  );
}

/** Remove the node at `path`. The root is never removed. */
export function removeAt(root: Filter, path: Path): Filter {
  if (path.length === 0) return root;
  return updateAt(root, path, () => null);
}

/**
 * Add a condition that narrows what the filter already says.
 *
 * Adding a filter to a list is "and also this" — the new condition is ANDed
 * with everything already there, whatever the root's combinator was. Under an
 * OR root a plain append would do the opposite: the new condition would join
 * the alternatives and widen the list, the way the date in #492 joined the
 * statuses it was meant to narrow, and the person would see more rows after
 * adding a condition. So an OR root is wrapped instead — `(a or b) and new` —
 * and its alternatives stay together as one group. An empty or single-child
 * root has nothing to widen, so it simply becomes an AND.
 *
 * It is what every add to the top of the filter does: the filter bar's field
 * dropdown, the mobile sheet and the tree editor's root "+ Condition". A
 * "+ Condition" inside a group is not one of them. It appends to that group
 * (`appendAt`), because there the group's own AND/OR is on screen, boxed in
 * around the person adding to it.
 */
export function addCondition(root: Filter, condition: FilterCondition): Filter {
  if (root.combinator === "and") return appendAt(root, [], condition);
  if (root.children.length <= 1) {
    return {
      ...root,
      combinator: "and",
      children: [...root.children, condition],
    };
  }
  return { kind: "group", combinator: "and", children: [root, condition] };
}

/**
 * Replace, add or remove the one top-level condition on `fieldName`.
 *
 * The mobile filter sheet (#349) edits one field at a time — pick "Payment
 * status", tick some boxes, done — so it never has a `Path`, only a field
 * name, and never wants more than one condition per field open at once.
 * `condition: null` removes it, same as `removeAt`; ticking the last box off
 * a field's options is the same shape as clearing it, so callers can pass
 * one straight through without checking first.
 *
 * "The condition on this field" only exists in an AND: under an OR the same
 * field twice is two alternatives, not one to replace, so there a new
 * condition is added (`addCondition`) and clearing finds nothing to clear.
 */
export function setFieldCondition(
  root: Filter,
  fieldName: string,
  condition: FilterCondition | null,
): Filter {
  const index =
    root.combinator === "and"
      ? root.children.findIndex(
          (child) => isCondition(child) && child.field === fieldName,
        )
      : -1;
  if (condition === null) {
    return index === -1 ? root : removeAt(root, [index]);
  }
  return index === -1
    ? addCondition(root, condition)
    : updateAt(root, [index], () => condition);
}
